import { CoObjectItem, SheetForm, TgkAmountObjectSource } from "../../interfaces/CoObject";
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Dimension } from "../../../api/models/dimension";
import LoadingManager from "../../../common/loading/LoadingManager";
import { AMOUNT_OBJECT_SOURCE } from "../../../utils/constants/notification";
import { SourceTypeEnum } from "../../../api/models/source-type-enum";
import { IChoiceGroupOption, IComboBoxOption } from "@fluentui/react";
import { ObjectPanelTabs } from "../co-panel/ObjectPanelComponent";
import { DimensionDetail } from "../../../api/models/dimension-detail";
import { DatasetField } from "../../../api/models/dataset-field";
import { Scenario } from "../../../api/models/scenario";
import { AvailableDimensions } from "../co-panel/ObjectPanelComponentHook";

export interface CoObjectSourceAmountProps {
  objectSource: TgkAmountObjectSource;
  updateCoObjectSourceAmount: (obj: TgkAmountObjectSource, tabValid, modified: boolean) => void;
  isEdit: boolean;
  isLocked: boolean;
  isComponentInstance: boolean;
  tab: ObjectPanelTabs;
  inheritableParameters: DimensionDetail[] | undefined;
  isFileExcelExisting?: boolean;
  fileExcelName?: string;
  updateFileExcelName?: (fileExcelName: string) => void;
  onPasteObject: (smartObject: CoObjectItem) => void;
  datasetFields: DatasetField[];
  originalSheetArea: SheetForm;
  setOriginalSheetArea?: Dispatch<SetStateAction<SheetForm>>;
  setIsOldSheetOrAreaSelected?: Dispatch<SetStateAction<boolean>>;
  setIsLoadingSheetAndArea?: Dispatch<SetStateAction<boolean>>;
  setIsSheetChartAndAreaToHide?: Dispatch<SetStateAction<boolean>>;
  isSheetChartAndAreaToHide?: boolean;
  referringScenarios: Scenario;
  availableDimensionsMetadata: AvailableDimensions;
}

const useCoObjectSourceAmountPanel = (props: CoObjectSourceAmountProps) => {
  const {
    objectSource: _objectSource,
    updateCoObjectSourceAmount,
    inheritableParameters,
    isFileExcelExisting,
    fileExcelName,
    updateFileExcelName,
    isEdit,
    tab,
    isComponentInstance,
    isLocked,
    datasetFields,
    availableDimensionsMetadata,
  } = props;

  //copying required to not propagate unwanted changes
  const objectSource = {
    ..._objectSource,
    type: _objectSource?.type ?? "AMOUNT",
    dimensions: _objectSource?.dimensions?.map((d): DimensionDetail => {
      return {
        ...d,
        values: d.values?.map((v) => v),
      };
    }),
    analyticalWorkspace: {
      code: _objectSource?.analyticalWorkspace?.code,
      description: _objectSource?.analyticalWorkspace?.description,
    },
    dataset: {
      code: _objectSource?.dataset?.code,
      description: _objectSource?.dataset?.description,
      partitionType: _objectSource?.dataset?.partitionType,
    },
    amount: _objectSource?.amount,
  };

  const [{ userSelectedDimensionsItems, isContextualMenuOpen }, setUserSelectedDimensionsState] = useState<{
    userSelectedDimensionsItems: Dimension[];
    isContextualMenuOpen: boolean;
  }>({
    userSelectedDimensionsItems: [],
    isContextualMenuOpen: false,
  });

  useEffect(() => {
    let objSource: TgkAmountObjectSource;
    if (
      objectSource?.objectSource !== SourceTypeEnum.EXCEL &&
      objectSource?.objectSource !== SourceTypeEnum.TAGETIK_FORM &&
      objectSource?.dbApplication
    ) {
      void selectDBAndFetchDefaultRoots({ key: objectSource.dbApplication.id, text: objectSource.dbApplication.desc });
      return () => LoadingManager.remove(AMOUNT_OBJECT_SOURCE);
    } else if (!objectSource) {
      objSource = {
        type: "AMOUNT",
        form: null,
        sheet: null,
        area: null,
        objectSource: SourceTypeEnum.TAGETIK,
        dbApplication: null,
        dimensions: [],
        analyticalWorkspace: null,
        dataset: null,
        amount: null,
      };
    } else {
      objSource = { ...objectSource };
    }
    updateCoObjectSourceAmount(
      objSource,
      validateFields(objSource, availableDimensionsMetadata.mandatoryDimensionsMetadata),
      false
    );
    return () => LoadingManager.remove(AMOUNT_OBJECT_SOURCE);
  }, []);

  useEffect(() => {
    if (
      objectSource?.objectSource !== SourceTypeEnum.EXCEL &&
      objectSource?.objectSource !== SourceTypeEnum.TAGETIK_FORM &&
      objectSource?.dbApplication?.id &&
      inheritableParameters &&
      availableDimensionsMetadata.mandatoryDimensionsMetadata &&
      !isEdit &&
      objectSource?.dimensions.length === 0
    ) {
      const objSource: TgkAmountObjectSource = {
        ...objectSource,
        dimensions: [],
      };
      objSource.dimensions = inheritableParameters
        ?.filter((d) =>
          availableDimensionsMetadata.mandatoryDimensionsMetadata.find((item) => d.dimensionOid === item.oid)
        )
        .map((p) => ({ ...p, inherited: true }));
      updateCoObjectSourceAmount(
        objSource,
        validateFields(objSource, availableDimensionsMetadata.mandatoryDimensionsMetadata),
        false
      );
    }

    return () => LoadingManager.remove(AMOUNT_OBJECT_SOURCE);
  }, [inheritableParameters, availableDimensionsMetadata.mandatoryDimensionsMetadata, objectSource?.dbApplication?.id]);

  async function selectDBAndFetchDefaultRoots(option: IComboBoxOption) {
    LoadingManager.create(AMOUNT_OBJECT_SOURCE);
    const modified = objectSource.dbApplication?.id !== option.key.toString();

    let obj: TgkAmountObjectSource = {
      ...objectSource,
      dbApplication: { id: String(option.key), desc: option.text },
      dimensions: modified ? [] : objectSource.dimensions,
    };

    try {
      const userDimensionsItems = availableDimensionsMetadata.optionalDimensionsMetadata.filter((dimension) => {
        return (
          objectSource.dimensions?.find((selectedDimension) => dimension.oid === selectedDimension.dimensionOid) !==
          undefined
        );
      });
      setUserSelectedDimensionsState((s) => ({
        ...s,
        userSelectedDimensionsItems: userDimensionsItems,
      }));

      updateCoObjectSourceAmount(
        obj,
        validateFields(obj, availableDimensionsMetadata.mandatoryDimensionsMetadata),
        modified
      );
    } catch (e) {
      // eslint-disable-next-line no-undef
      console.error(e);
      updateCoObjectSourceAmount(
        obj,
        validateFields(obj, availableDimensionsMetadata.mandatoryDimensionsMetadata),
        modified
      );
    } finally {
      LoadingManager.remove(AMOUNT_OBJECT_SOURCE);
    }
  }

  function updateDimensionFromTree(dimensionItem: Dimension, newDimensions: DimensionDetail[] | undefined) {
    const dimensionToAdd = newDimensions?.map((newDimension) => ({ ...newDimension, dimensionOid: dimensionItem.oid }));

    const newSourceDimensions = objectSource.dimensions.filter((d) => d.dimensionOid !== dimensionItem.oid);
    newSourceDimensions.push(...dimensionToAdd);
    const obj: TgkAmountObjectSource = {
      ...objectSource,
      dimensions: newSourceDimensions,
    };

    updateCoObjectSourceAmount(obj, validateFields(obj, availableDimensionsMetadata.mandatoryDimensionsMetadata), true);
  }

  function addDimension(d: Dimension) {
    setUserSelectedDimensionsState((s) => ({
      ...s,
      isContextualMenuOpen: false,
      userSelectedDimensionsItems: [...s.userSelectedDimensionsItems, d],
    }));
    const thisDimensionParameters = inheritableParameters
      .filter((p) => d.oid === p.dimensionOid)
      .map((p) => ({ ...p, inherited: true }));
    const obj: TgkAmountObjectSource = {
      ...objectSource,
      dimensions: [...objectSource.dimensions, ...thisDimensionParameters],
    };
    updateCoObjectSourceAmount(obj, validateFields(obj, availableDimensionsMetadata.mandatoryDimensionsMetadata), true);
  }

  function removeDimension(dimensionOid: string) {
    const obj: TgkAmountObjectSource = {
      ...objectSource,
      dimensions: objectSource.dimensions.filter((d) => d.dimensionOid !== dimensionOid),
    };
    updateCoObjectSourceAmount(obj, validateFields(obj, availableDimensionsMetadata.mandatoryDimensionsMetadata), true);
  }

  function validateFields(obj: TgkAmountObjectSource, dimensionsItems?: Dimension[]): ObjectPanelTabs {
    let isValid = true;
    if (obj == null) {
      isValid = false;
    } else {
      const { dbApplication, dimensions } = obj;
      switch (obj?.objectSource) {
        case SourceTypeEnum.TAGETIK_FORM:
          return validateTgkFormSource(obj);
        case SourceTypeEnum.EXCEL:
          return validateExcelSource(obj);
        default:
          if (obj.type === "AMOUNT_AIH") {
            return validateAnalytical(obj);
          } else {
            // @ts-ignore
            if (dbApplication == null || dbApplication === "") {
              isValid = false;
            }
            if (!dimensionsItems?.length) {
              isValid = false;
            }
            dimensionsItems?.forEach((mandatoryDimensionRoot) => {
              if (!dimensions?.find((d) => d.dimensionOid === mandatoryDimensionRoot.oid)?.values?.length) {
                isValid = false;
              }
            });
          }
      }
    }
    return { key: props.tab.key, name: props.tab.name, isValid: isValid };
  }

  const validateExcelSource = (obj: TgkAmountObjectSource): ObjectPanelTabs => {
    const isValid = validateCommonSource(obj);
    return { key: props.tab.key, name: props.tab.name, isValid: isValid };
  };

  const validateTgkFormSource = (obj: TgkAmountObjectSource): ObjectPanelTabs => {
    const isValid = validateCommonSource(obj) && obj.dbApplication != null;
    return { key: props.tab.key, name: props.tab.name, isValid: isValid };
  };

  const validateAnalytical = (obj: TgkAmountObjectSource): ObjectPanelTabs => {
    const tab = { key: props.tab.key, name: props.tab.name, isValid: false };
    if (obj == null) return tab;
    const { dbApplication, analyticalWorkspace, dataset, amount } = obj;
    if (!dbApplication) return tab;
    if (!analyticalWorkspace) return tab;
    if (!dataset) return tab;
    if (!amount) return tab;
    return { ...tab, isValid: true };
  };

  const validateCommonSource = (obj: TgkAmountObjectSource): boolean => {
    if (!obj) return false;

    const { form, sheet, area, cell, type } = obj;
    return (
      form?.oid != null &&
      sheet?.desc != null &&
      type != null &&
      objectSource != null &&
      !isAreaAndCellEmpty(area, cell)
    );
  };

  const isAreaAndCellEmpty = (area, cell) => {
    return !area?.name && !cell;
  };

  const filteredDimensionsRoot = availableDimensionsMetadata.optionalDimensionsMetadata.filter(
    (dimension) => userSelectedDimensionsItems.find((presentItem) => dimension.desc === presentItem.desc) === undefined //not default and not previously selected
  );

  const onChangeFormattingAs = (_: React.SyntheticEvent<HTMLElement>, option: IChoiceGroupOption) => {
    const selectedOption = String(option.key);
    const obj = {
      type: "AMOUNT",
      objectSource: selectedOption,
      dbApplication: null,
      dimensions: null,
    } as TgkAmountObjectSource;

    objectSource.objectSource = selectedOption;
    updateCoObjectSourceAmount(obj, validateFields(obj, []), true);
  };

  const isExcelSource = () => {
    return objectSource.objectSource === SourceTypeEnum.EXCEL;
  };

  const isTgkSource = () => {
    return (
      objectSource.objectSource === undefined ||
      objectSource.objectSource.length == 0 ||
      objectSource.objectSource === SourceTypeEnum.TAGETIK
    );
  };

  const isTgkFormSource = () => {
    return objectSource.objectSource === SourceTypeEnum.TAGETIK_FORM;
  };

  const linkRef = React.useRef(null);
  return {
    isEdit,
    isLocked,
    tab,
    isComponentInstance,
    objectSource,
    datasetFields,
    isExcelSource,
    validateExcelSource,
    isTgkSource,
    isTgkFormSource,
    validateTgkFormSource,
    onChangeFormattingAs,
    updateCoObjectSourceAmount,
    selectDBAndFetchDefaultRoots,
    inheritableParameters,
    validateFields,
    validateAnalytical,
    updateDimensionFromTree,
    userSelectedDimensionsItems,
    removeDimension,
    filteredDimensionsRoot,
    linkRef,
    addDimension,
    setUserSelectedDimensionsState,
    isContextualMenuOpen,
    isFileExcelExisting,
    fileExcelName,
    updateFileExcelName,
  };
};
export default useCoObjectSourceAmountPanel;
